'use client'

import { useState, useEffect, type ReactNode } from 'react'
import { Eye, Minus, Plus, X } from 'lucide-react'
import type { PreviewEntry } from '../trustCenterTypes'

export function CttPreview({
  entry,
  tab,
  reportSlot,
  onClose,
}: {
  entry: PreviewEntry | null
  tab: string
  reportSlot?: ReactNode
  /** Renders a close button in the header — used when CttPreview is the sole content of a modal (e.g. the Alertas Phishing article view) rather than an inline page panel. */
  onClose?: () => void
}) {
  const [open, setOpen] = useState(true)

  useEffect(() => { setOpen(true) }, [entry?.title])

  if (!entry) return null

  return (
    <div className="mx-6 mt-4 mb-0 border border-border bg-card text-foreground shadow-sm">
      <div className="flex items-center gap-2 border-b border-border bg-secondary px-4 py-2">
        <Eye className="h-3 w-3 text-muted-foreground" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Preview · {tab}
        </span>
        {/* The TF badge marks this as an internal preview panel — dropped
            when onClose is set (the article-view modal), since that's meant
            to be a faithful stand-in for the real public page, which has no
            such watermark. */}
        {!onClose && <img src="/logoTF.png" alt="" className="ml-auto h-4 w-auto opacity-20" />}
        {onClose && (
          <button
            onClick={onClose}
            className="ml-auto shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:text-muted-foreground"
            aria-label="Close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="border-b border-border">
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left transition-colors hover:bg-slate-50/60"
        >
          <span className="text-sm font-medium text-foreground">
            {entry.date} - {entry.title}
          </span>
          <span className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground">
            {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          </span>
        </button>
        {open && (
          <div className="space-y-3 px-4 pb-5 pt-1">
            {entry.paragraphs.map((p, i) => (
              <p key={i} className="text-sm leading-relaxed text-muted-foreground">{p}</p>
            ))}
            {(entry.images.length > 0 || reportSlot) && (
              <div className="flex flex-wrap gap-3 pt-1 items-start">
                {reportSlot && (
                  <div
                    className="overflow-hidden rounded border border-border bg-secondary shrink-0"
                    style={{ height: 256, width: 141 }}
                  >
                    <div style={{ transform: 'scale(0.44)', transformOrigin: 'top left', width: 320 }}>
                      {reportSlot}
                    </div>
                  </div>
                )}
                {entry.images.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt=""
                    loading="lazy"
                    className="max-h-64 w-auto rounded border border-border bg-secondary object-contain"
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
